import { useCallback, useEffect, useRef } from 'react';

const soundLevel = () => {
  try {
    const settings = JSON.parse(localStorage.getItem('gv_keno_settings')) || {};
    return settings.muted ? 0 : (Number.isFinite(settings.volume) ? Math.max(0, Math.min(100, settings.volume)) : 60) / 100;
  } catch { return .6; }
};

export const useTipNotificationSound = () => {
  const audio = useRef(null);
  useEffect(() => {
    // Unlock only during a real gesture. Never queue old alerts for later playback.
    const unlock = () => {
      const Audio = window.AudioContext || window.webkitAudioContext;
      if (!Audio) return;
      try {
        if (!audio.current || audio.current.state === 'closed') audio.current = new Audio();
        if (audio.current.state === 'suspended') audio.current.resume().catch(() => {});
      } catch { /* Visual notifications remain available without browser audio. */ }
    };
    window.addEventListener('pointerdown', unlock, { capture: true, passive: true });
    window.addEventListener('keydown', unlock, true);
    return () => {
      window.removeEventListener('pointerdown', unlock, true);
      window.removeEventListener('keydown', unlock, true);
      const context = audio.current;
      audio.current = null;
      if (context && context.state !== 'closed') context.close().catch(() => {});
    };
  }, []);
  return useCallback((notificationId) => {
    const context = audio.current, level = soundLevel();
    if (!level || document.hidden || context?.state !== 'running') return;
    try {
      const now = context.currentTime;
      // Original, short two-note credit chime; no remote audio assets or requests.
      [783.99, 1046.5].forEach((frequency, index) => {
        const voice = context.createOscillator(), gain = context.createGain();
        const start = now + index * .12;
        voice.type = 'sine'; voice.frequency.setValueAtTime(frequency, start);
        gain.gain.setValueAtTime(.0001, start);
        gain.gain.exponentialRampToValueAtTime(.1 * level, start + .012);
        gain.gain.exponentialRampToValueAtTime(.0001, start + .32);
        voice.connect(gain); gain.connect(context.destination);
        voice.start(start); voice.stop(start + .34);
        voice.onended = () => { voice.disconnect(); gain.disconnect(); };
      });
      window.dispatchEvent(new CustomEvent('gv-tip-sound', { detail: { notificationId, volume: level * 100 } }));
    } catch { /* A sound failure must never interrupt a tip or its alert. */ }
  }, []);
};